/**
 * Sketch Engine - 2D Sketching System for CAD
 * Handles canvas-based 2D drawing with geometric entities and constraints
 */

class SketchEngine {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.viewer = cadViewer.viewer;
    this.engine = cadViewer.engine;

    // Canvas elements
    this.canvasContainer = null;
    this.canvas = null;
    this.ctx = null;

    // Current sketch state
    this.activeSketch = null;
    this.activeTool = null;
    this.sketchPlane = null;

    // Drawing state
    this.isDrawing = false;
    this.startPoint = null;
    this.currentPoint = null;
    this.tempEntity = null;
    this.snapMode = true; // Snap to anchor points
    this.snapRadius = 0.2; // Snap distance in world units
    this.constructionMode = false; // Construction geometry mode

    // Sketch entities
    this.entities = [];
    this.lastEndPoint = null; // Track last endpoint for chaining
    this.snapPoint = null; // Current snap target
    this.selectedEntities = []; // Selected entities

    // Camera and projection
    this.camera2D = null;
    this.zoom = 1.0;
    this.panOffset = { x: 0, y: 0 };

    this.initialize();
  }

  initialize() {
    this.createCanvasOverlay();
    this.setupEventHandlers();
  }

  createCanvasOverlay() {
    // Create canvas container if it doesn't exist
    let container = document.getElementById('sketchCanvasContainer');

    if (!container) {
      container = document.createElement('div');
      container.id = 'sketchCanvasContainer';
      container.className = 'sketch-canvas-container';
      container.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 100;
        display: none;
      `;

      const viewport = document.getElementById('viewport');
      if (viewport && viewport.parentElement) {
        viewport.parentElement.insertBefore(container, viewport.nextSibling);
      }
    }

    this.canvasContainer = container;

    // Create canvas if it doesn't exist
    let canvas = document.getElementById('sketchCanvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = 'sketchCanvas';
      canvas.className = 'sketch-canvas';
      canvas.style.cssText = `
        width: 100%;
        height: 100%;
        cursor: crosshair;
      `;
      container.appendChild(canvas);
    }

    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas() {
    if (!this.canvas) return;

    const viewport = document.getElementById('viewport');
    this.canvas.width = viewport.clientWidth;
    this.canvas.height = viewport.clientHeight;

    if (this.activeSketch) {
      this.render();
    }
  }

  setupEventHandlers() {
    if (!this.canvas) return;

    this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.canvas.addEventListener('mouseup', (e) => this.onMouseUp(e));
    this.canvas.addEventListener('dblclick', (e) => this.onDoubleClick(e));

    document.addEventListener('keydown', (e) => this.onKeyDown(e));
  }

  // ===== Sketch Activation =====

  startSketch(plane) {
    console.log('[SketchEngine] Starting sketch on plane:', plane);

    this.sketchPlane = plane;
    this.activeSketch = {
      id: Utils.generateId(),
      name: `Sketch${this.entities.length + 1}`,
      plane: plane,
      entities: []
    };

    this.entities = [];

    // Show canvas
    this.canvasContainer.style.display = 'block';
    this.canvasContainer.style.pointerEvents = 'auto';

    // Align camera to sketch plane
    this.alignCameraToPlane(plane);

    this.render();
  }

  finishSketch() {
    if (!this.activeSketch) return;

    console.log('[SketchEngine] Finishing sketch with', this.entities.length, 'entities');

    // Add entities to sketch
    this.activeSketch.entities = [...this.entities];

    // Hide canvas
    this.canvasContainer.style.display = 'none';
    this.canvasContainer.style.pointerEvents = 'none';

    // Create 3D representation
    this.create3DSketch(this.activeSketch);

    // Check if this is editing an existing sketch
    const existingFeature = this.cadViewer.document.getFeature(this.activeSketch.id);

    if (existingFeature) {
      // Capture old state before updating
      const oldEntities = JSON.parse(JSON.stringify(existingFeature.parameters.entities || []));
      const oldConstraints = JSON.parse(JSON.stringify(existingFeature.parameters.constraints || []));

      // Update existing sketch
      existingFeature.parameters.entities = [...this.entities];
      this.cadViewer.document.hasUnsavedChanges = true;

      // Record sketch edit in history
      if (this.cadViewer.historyManager) {
        this.cadViewer.historyManager.recordOperation({
          type: 'edit_sketch',
          name: `Edit ${existingFeature.name}`,
          sketchId: existingFeature.id,
          oldEntities: oldEntities,
          oldConstraints: oldConstraints,
          newEntities: JSON.parse(JSON.stringify(this.entities)),
          newConstraints: JSON.parse(JSON.stringify(existingFeature.parameters.constraints || []))
        });
      }

      Utils.notify(`Sketch updated with ${this.entities.length} entities`, 'success');

      // Recompute dependent features (like extrusions)
      this.cadViewer.recomputeDependentFeatures(this.activeSketch.id);
    } else {
      // Create new Feature from sketch
      const feature = new Feature(
        this.activeSketch.id,
        'sketch',
        {
          name: this.activeSketch.name,
          plane: this.sketchPlane,
          entities: this.entities
        }
      );

      this.cadViewer.document.addFeature(feature);

      // Record new sketch in history
      if (this.cadViewer.historyManager) {
        this.cadViewer.historyManager.recordOperation({
          type: 'add_sketch',
          name: `Create ${feature.name}`,
          sketchId: feature.id,
          sketch: JSON.parse(JSON.stringify(feature))
        });
      }

      // Auto-select the new sketch
      this.cadViewer.selectFeature(feature.id);

      Utils.notify(`Sketch completed with ${this.entities.length} entities`, 'success');
    }

    // Update feature tree
    this.cadViewer.updateFeatureTree();

    this.activeSketch = null;
    this.activeTool = null;
    this.entities = [];
  }

  /**
   * Edit an existing sketch
   */
  editSketch(featureId) {
    const feature = this.cadViewer.document.getFeature(featureId);
    if (!feature || feature.type !== 'sketch') {
      Utils.notify('Invalid sketch to edit', 'error');
      return;
    }

    console.log('[SketchEngine] Editing sketch:', feature.name);

    // Load existing sketch data
    this.activeSketch = {
      id: feature.id,
      name: feature.name || 'Sketch'
    };

    this.sketchPlane = feature.parameters.plane;
    this.entities = [...feature.parameters.entities]; // Clone entities

    // Hide dependent features (like extrusions)
    this.cadViewer.hideDependentFeatures(featureId);

    // Show canvas
    this.canvasContainer.style.display = 'block';
    this.canvasContainer.style.pointerEvents = 'auto';

    // Set initial tool
    this.activeTool = 'line';

    // Render existing entities
    this.render();

    Utils.notify(`Editing ${feature.name} - Press Enter to finish, ESC to cancel`, 'info');
  }

  cancelSketch() {
    console.log('[SketchEngine] Cancelling sketch');

    this.canvasContainer.style.display = 'none';
    this.canvasContainer.style.pointerEvents = 'none';

    this.activeSketch = null;
    this.activeTool = null;
    this.entities = [];
    this.isDrawing = false;

    Utils.notify('Sketch cancelled', 'info');
  }

  alignCameraToPlane(plane) {
    const camera = this.engine.camera;

    // Position camera perpendicular to plane
    const distance = 5;
    const normal = new THREE.Vector3(plane.normal.x, plane.normal.y, plane.normal.z);
    const origin = new THREE.Vector3(plane.origin.x, plane.origin.y, plane.origin.z);

    camera.position.copy(origin).addScaledVector(normal, distance);
    camera.lookAt(origin);
    camera.up.set(plane.xDirection.x, plane.xDirection.y, plane.xDirection.z);

    if (this.engine.controls) {
      this.engine.controls.target.copy(origin);
      this.engine.controls.update();
    }
  }

  // ===== Tool Activation =====

  activateTool(toolName) {
    console.log('[SketchEngine] Activating tool:', toolName);

    this.activeTool = toolName;
    this.isDrawing = false;
    this.startPoint = null;
    this.currentPoint = null;
    this.tempEntity = null;

    // If no active sketch, start one on XY plane
    if (!this.activeSketch) {
      this.startSketch(SketchPlane.XY());
    }

    Utils.notify(`Activated ${toolName} tool`, 'info');
  }

  // ===== Mouse Event Handlers =====

  onMouseDown(e) {
    if (!this.activeTool) return;

    let point = this.getCanvasPoint(e);

    // Apply snap if enabled
    if (this.snapMode) {
      const snapTarget = this.findNearestAnchor(point);
      if (snapTarget) {
        point = snapTarget.point;
        console.log('[SketchEngine] Snapped to', snapTarget.type);
      }
    }

    this.startPoint = point;
    this.isDrawing = true;

    console.log('[SketchEngine] Mouse down at', point);
  }

  onMouseMove(e) {
    if (!this.activeTool) return;

    let point = this.getCanvasPoint(e);

    // Check for snap targets
    this.snapPoint = null;
    if (this.snapMode) {
      const snapTarget = this.findNearestAnchor(point);
      if (snapTarget) {
        this.snapPoint = snapTarget;
        point = snapTarget.point;
      }
    }

    this.currentPoint = point;

    if (this.isDrawing) {
      // Update temporary entity
      this.updateTempEntity();
    }

    this.render();
  }

  onMouseUp(e) {
    if (!this.activeTool || !this.isDrawing) return;

    const point = this.getCanvasPoint(e);
    this.isDrawing = false;

    console.log('[SketchEngine] Mouse up at', point);

    // Create final entity
    const entity = this.createEntity(this.startPoint, point);

    // Store last endpoint for chain mode
    if (entity && this.chainMode) {
      if (entity.type === 'line') {
        this.lastEndPoint = entity.end;
      } else if (entity.type === 'rectangle') {
        this.lastEndPoint = entity.end;
      }
    }

    // Don't clear startPoint in chain mode for lines
    if (!this.chainMode || this.activeTool !== 'line') {
      this.startPoint = null;
      this.lastEndPoint = null;
    }

    this.currentPoint = null;
    this.tempEntity = null;

    // Render immediately to show the new entity
    this.render();

    // Don't update 3D while sketching - only after finishing
    // This prevents ugly overlapping
  }

  onDoubleClick(e) {
    // Finish sketch on double-click
    if (this.activeSketch && this.entities.length > 0) {
      this.finishSketch();
    }
  }

  onKeyDown(e) {
    // SPACE to toggle between sketch and rotate mode
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      this.toggleCanvasInteraction();
      return;
    }

    if (!this.activeSketch) return;

    // ESC to cancel
    if (e.key === 'Escape') {
      this.cancelSketch();
    }

    // Enter to finish
    if (e.key === 'Enter' && this.entities.length > 0) {
      this.finishSketch();
    }
  }

  /**
   * Toggle canvas interaction (for 3D rotation)
   */
  toggleCanvasInteraction() {
    const isActive = this.canvasContainer.style.pointerEvents === 'auto';
    const statusEl = document.getElementById('interactionModeStatus');
    const btnMode = document.getElementById('btnToggleMode');

    if (isActive) {
      // Disable canvas, enable 3D rotation
      this.canvasContainer.style.pointerEvents = 'none';
      if (statusEl) statusEl.textContent = 'Rotate Mode - Press SPACE to sketch';
      if (btnMode) btnMode.style.background = 'rgba(255, 165, 0, 0.2)';
      Utils.notify('Rotate mode - Drag to rotate view', 'info');
    } else {
      // Enable canvas, disable 3D rotation
      this.canvasContainer.style.pointerEvents = 'auto';
      if (statusEl) statusEl.textContent = 'Sketch Mode - Press SPACE to rotate';
      if (btnMode) btnMode.style.background = 'rgba(0, 122, 255, 0.2)';
      Utils.notify('Sketch mode - Click to draw', 'info');
    }
  }

  // ===== Entity Creation =====

  createEntity(start, end) {
    let entity = null;

    switch (this.activeTool) {
      case 'line':
        entity = {
          type: 'line',
          id: Utils.generateId(),
          start: { ...start },
          end: { ...end },
          construction: this.constructionMode
        };
        break;

      case 'rectangle':
        entity = {
          type: 'rectangle',
          id: Utils.generateId(),
          start: { ...start },
          end: { ...end },
          width: Math.abs(end.x - start.x),
          height: Math.abs(end.y - start.y),
          construction: this.constructionMode
        };
        break;

      case 'circle':
        const radius = Math.sqrt(
          Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        );
        entity = {
          type: 'circle',
          id: Utils.generateId(),
          center: { ...start },
          radius: radius,
          construction: this.constructionMode
        };
        break;

      case 'point':
        entity = {
          type: 'point',
          id: Utils.generateId(),
          position: { ...start },
          construction: this.constructionMode
        };
        break;

      case 'arc':
        // 3-point arc: start, end, and we'll add midpoint later
        // For now, create arc from start to end
        const arcRadius = Math.sqrt(
          Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        ) / 2;
        const arcCenter = {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2,
          z: (start.z + end.z) / 2
        };
        entity = {
          type: 'arc',
          id: Utils.generateId(),
          center: arcCenter,
          start: { ...start },
          end: { ...end },
          radius: arcRadius,
          startAngle: 0,
          endAngle: Math.PI,
          construction: this.constructionMode
        };
        break;

      case 'spline':
        // Spline needs multiple points - for now create simple bezier
        entity = {
          type: 'spline',
          id: Utils.generateId(),
          points: [{ ...start }, { ...end }],
          controlPoints: [],
          construction: this.constructionMode
        };
        break;

      case 'text':
        const text = prompt('Enter text:', 'Text');
        if (text) {
          entity = {
            type: 'text',
            id: Utils.generateId(),
            position: { ...start },
            text: text,
            fontSize: 0.5,
            construction: this.constructionMode
          };
        }
        break;

      case 'ellipse':
        const rx = Math.abs(end.x - start.x);
        const ry = Math.abs(end.y - start.y);
        entity = {
          type: 'ellipse',
          id: Utils.generateId(),
          center: {
            x: (start.x + end.x) / 2,
            y: (start.y + end.y) / 2,
            z: start.z
          },
          radiusX: rx,
          radiusY: ry,
          construction: this.constructionMode
        };
        break;

      case 'polygon':
        const sides = parseInt(prompt('Number of sides:', '6'));
        if (isNaN(sides) || sides < 3) break;

        const polyRadius = Math.sqrt(
          Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        );
        entity = {
          type: 'polygon',
          id: Utils.generateId(),
          center: { ...start },
          radius: polyRadius,
          sides: sides,
          construction: this.constructionMode
        };
        break;

      case 'slot':
        const slotWidth = Math.abs(end.y - start.y);
        const slotLength = Math.abs(end.x - start.x);
        entity = {
          type: 'slot',
          id: Utils.generateId(),
          start: { ...start },
          end: { ...end },
          width: slotWidth,
          length: slotLength,
          construction: this.constructionMode
        };
        break;
    }

    if (entity) {
      this.entities.push(entity);
      console.log('[SketchEngine] Created entity:', entity.type);

      // Record in history
      if (this.cadViewer.historyManager && this.activeSketch) {
        this.cadViewer.historyManager.recordOperation({
          type: 'add_sketch_entity',
          name: `Add ${entity.type}`,
          sketchId: this.activeSketch.id,
          entityId: entity.id,
          entity: JSON.parse(JSON.stringify(entity))
        });
      }
    }

    return entity;
  }

  updateTempEntity() {
    if (!this.startPoint || !this.currentPoint) return;

    switch (this.activeTool) {
      case 'line':
        this.tempEntity = {
          type: 'line',
          start: this.startPoint,
          end: this.currentPoint
        };
        break;

      case 'rectangle':
        this.tempEntity = {
          type: 'rectangle',
          start: this.startPoint,
          end: this.currentPoint
        };
        break;

      case 'circle':
        const radius = Math.sqrt(
          Math.pow(this.currentPoint.x - this.startPoint.x, 2) +
          Math.pow(this.currentPoint.y - this.startPoint.y, 2)
        );
        this.tempEntity = {
          type: 'circle',
          center: this.startPoint,
          radius: radius
        };
        break;

      case 'arc':
        const arcRadius = Math.sqrt(
          Math.pow(this.currentPoint.x - this.startPoint.x, 2) +
          Math.pow(this.currentPoint.y - this.startPoint.y, 2)
        ) / 2;
        const arcCenter = {
          x: (this.startPoint.x + this.currentPoint.x) / 2,
          y: (this.startPoint.y + this.currentPoint.y) / 2,
          z: (this.startPoint.z + this.currentPoint.z) / 2
        };
        this.tempEntity = {
          type: 'arc',
          center: arcCenter,
          start: this.startPoint,
          end: this.currentPoint,
          radius: arcRadius
        };
        break;

      case 'spline':
        this.tempEntity = {
          type: 'spline',
          points: [this.startPoint, this.currentPoint]
        };
        break;

      case 'point':
        this.tempEntity = {
          type: 'point',
          position: this.currentPoint
        };
        break;

      case 'ellipse':
        const rx = Math.abs(this.currentPoint.x - this.startPoint.x);
        const ry = Math.abs(this.currentPoint.y - this.startPoint.y);
        this.tempEntity = {
          type: 'ellipse',
          center: {
            x: (this.startPoint.x + this.currentPoint.x) / 2,
            y: (this.startPoint.y + this.currentPoint.y) / 2,
            z: this.startPoint.z
          },
          radiusX: rx,
          radiusY: ry
        };
        break;

      case 'polygon':
        const polyRadius = Math.sqrt(
          Math.pow(this.currentPoint.x - this.startPoint.x, 2) +
          Math.pow(this.currentPoint.y - this.startPoint.y, 2)
        );
        this.tempEntity = {
          type: 'polygon',
          center: this.startPoint,
          radius: polyRadius,
          sides: 6 // Default, will be set on creation
        };
        break;

      case 'slot':
        this.tempEntity = {
          type: 'slot',
          start: this.startPoint,
          end: this.currentPoint,
          width: Math.abs(this.currentPoint.y - this.startPoint.y),
          length: Math.abs(this.currentPoint.x - this.startPoint.x)
        };
        break;
    }
  }

  /**
   * Trim/Extend operation - trim entity to boundary or extend to meet another entity
   */
  trimExtend(entityId, boundaryId, isTrim = true) {
    const entity = this.entities.find(e => e.id === entityId);
    const boundary = this.entities.find(e => e.id === boundaryId);

    if (!entity || !boundary) {
      Utils.notify('Select entity and boundary', 'warning');
      return;
    }

    // Calculate intersection point
    // This is a simplified implementation - full CAD would handle all entity type combinations
    if (entity.type === 'line' && boundary.type === 'line') {
      const intersection = this.lineLineIntersection(entity, boundary);
      if (intersection) {
        if (isTrim) {
          // Trim line to intersection
          entity.end = intersection;
        } else {
          // Extend line to intersection
          entity.end = intersection;
        }
        this.render();
        Utils.notify(isTrim ? 'Trimmed entity' : 'Extended entity', 'success');
      }
    }
  }

  /**
   * Calculate intersection between two lines
   */
  lineLineIntersection(line1, line2) {
    const x1 = line1.start.x, y1 = line1.start.y;
    const x2 = line1.end.x, y2 = line1.end.y;
    const x3 = line2.start.x, y3 = line2.start.y;
    const x4 = line2.end.x, y4 = line2.end.y;

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 1e-10) return null; // Parallel lines

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1),
      z: line1.start.z
    };
  }

  /**
   * Offset curve operation - create parallel curve at specified distance
   */
  offsetCurve(entityId, distance) {
    const entity = this.entities.find(e => e.id === entityId);
    if (!entity) {
      Utils.notify('Select entity to offset', 'warning');
      return;
    }

    let offsetEntity = null;

    switch (entity.type) {
      case 'line':
        // Offset line perpendicular to its direction
        const dx = entity.end.x - entity.start.x;
        const dy = entity.end.y - entity.start.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        const nx = -dy / length * distance; // Normal direction
        const ny = dx / length * distance;

        offsetEntity = {
          type: 'line',
          id: Utils.generateId(),
          start: {
            x: entity.start.x + nx,
            y: entity.start.y + ny,
            z: entity.start.z
          },
          end: {
            x: entity.end.x + nx,
            y: entity.end.y + ny,
            z: entity.end.z
          }
        };
        break;

      case 'circle':
        // Offset circle by changing radius
        offsetEntity = {
          type: 'circle',
          id: Utils.generateId(),
          center: { ...entity.center },
          radius: entity.radius + distance
        };
        break;

      case 'arc':
        // Offset arc by changing radius
        offsetEntity = {
          type: 'arc',
          id: Utils.generateId(),
          center: { ...entity.center },
          start: { ...entity.start },
          end: { ...entity.end },
          radius: entity.radius + distance,
          startAngle: entity.startAngle,
          endAngle: entity.endAngle
        };
        break;
    }

    if (offsetEntity) {
      this.entities.push(offsetEntity);
      this.render();
      Utils.notify(`Offset created (distance: ${distance})`, 'success');
    }
  }

  // ===== Coordinate Conversion =====

  getCanvasPoint(event) {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Convert to world coordinates using raycasting
    return this.canvasToWorld(x, y);
  }

  canvasToWorld(canvasX, canvasY) {
    const rect = this.canvas.getBoundingClientRect();

    // Normalized device coordinates
    const mouse = new THREE.Vector2(
      (canvasX / rect.width) * 2 - 1,
      -(canvasY / rect.height) * 2 + 1
    );

    // Raycast to sketch plane
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    const normal = new THREE.Vector3(
      this.sketchPlane.normal.x,
      this.sketchPlane.normal.y,
      this.sketchPlane.normal.z
    );
    const origin = new THREE.Vector3(
      this.sketchPlane.origin.x,
      this.sketchPlane.origin.y,
      this.sketchPlane.origin.z
    );

    const plane = new THREE.Plane(normal, -origin.dot(normal));
    const intersection = new THREE.Vector3();
    raycaster.ray.intersectPlane(plane, intersection);

    return {
      x: intersection.x,
      y: intersection.y,
      z: intersection.z
    };
  }

  worldToCanvas(worldPoint) {
    const vector = new THREE.Vector3(worldPoint.x, worldPoint.y, worldPoint.z);
    vector.project(this.engine.camera);

    const x = (vector.x + 1) / 2 * this.canvas.width;
    const y = -(vector.y - 1) / 2 * this.canvas.height;

    return { x, y };
  }

  // ===== Snap System =====

  /**
   * Find nearest anchor point (endpoint, midpoint, circle center)
   */
  findNearestAnchor(point) {
    if (!this.snapMode || this.entities.length === 0) return null;

    const anchors = this.getAllAnchors();
    let nearest = null;
    let minDist = this.snapRadius;

    anchors.forEach(anchor => {
      const dist = Math.sqrt(
        Math.pow(anchor.point.x - point.x, 2) +
        Math.pow(anchor.point.y - point.y, 2) +
        Math.pow(anchor.point.z - point.z, 2)
      );

      if (dist < minDist) {
        minDist = dist;
        nearest = anchor;
      }
    });

    return nearest;
  }

  /**
   * Get all anchor points from existing entities
   */
  getAllAnchors() {
    const anchors = [];

    this.entities.forEach(entity => {
      switch (entity.type) {
        case 'point':
          anchors.push({
            type: 'point',
            entityId: entity.id,
            point: entity.position
          });
          break;

        case 'line':
          // Endpoints
          anchors.push({
            type: 'endpoint',
            entityId: entity.id,
            point: entity.start
          });
          anchors.push({
            type: 'endpoint',
            entityId: entity.id,
            point: entity.end
          });
          // Midpoint
          anchors.push({
            type: 'midpoint',
            entityId: entity.id,
            point: {
              x: (entity.start.x + entity.end.x) / 2,
              y: (entity.start.y + entity.end.y) / 2,
              z: (entity.start.z + entity.end.z) / 2
            }
          });
          break;

        case 'circle':
        case 'arc':
          // Center
          anchors.push({
            type: 'center',
            entityId: entity.id,
            point: entity.center
          });
          break;

        case 'rectangle':
          // 4 corners
          const corners = [
            entity.start,
            { x: entity.end.x, y: entity.start.y, z: entity.start.z },
            entity.end,
            { x: entity.start.x, y: entity.end.y, z: entity.start.z }
          ];
          corners.forEach((corner, i) => {
            anchors.push({
              type: 'corner',
              entityId: entity.id,
              point: corner
            });
          });
          break;
      }
    });

    return anchors;
  }

  /**
   * Toggle snap mode
   */
  toggleSnapMode() {
    this.snapMode = !this.snapMode;

    const statusEl = document.getElementById('snapModeStatus');
    const btnSnap = document.getElementById('btnToggleSnap');

    if (this.snapMode) {
      statusEl.textContent = 'Snap: ON - Click anchors to connect';
      btnSnap.style.background = 'rgba(191, 90, 242, 0.2)';
    } else {
      statusEl.textContent = 'Snap: OFF - Free placement';
      btnSnap.style.background = 'rgba(255, 255, 255, 0.1)';
    }

    console.log('[SketchEngine] Snap mode:', this.snapMode ? 'ON' : 'OFF');
  }

  /**
   * Toggle construction mode
   */
  toggleConstructionMode() {
    this.constructionMode = !this.constructionMode;

    const statusEl = document.getElementById('constructionModeStatus');
    const btnConstruction = document.getElementById('btnToggleConstruction');

    if (this.constructionMode) {
      if (statusEl) statusEl.textContent = 'Construction Mode: ON - Helper geometry';
      if (btnConstruction) {
        btnConstruction.style.background = 'rgba(255, 165, 0, 0.3)';
        btnConstruction.style.borderColor = '#FFA500';
      }
      Utils.notify('Construction mode ON - Creating helper geometry', 'info');
    } else {
      if (statusEl) statusEl.textContent = 'Construction Mode: OFF';
      if (btnConstruction) {
        btnConstruction.style.background = 'rgba(255, 255, 255, 0.1)';
        btnConstruction.style.borderColor = 'rgba(255, 255, 255, 0.3)';
      }
      Utils.notify('Construction mode OFF - Creating regular geometry', 'info');
    }

    console.log('[SketchEngine] Construction mode:', this.constructionMode ? 'ON' : 'OFF');
  }

  // ===== Rendering =====

  render() {
    if (!this.ctx) return;

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Set drawing style
    this.ctx.strokeStyle = '#BF5AF2'; // Apple purple
    this.ctx.lineWidth = 2;
    this.ctx.fillStyle = 'rgba(191, 90, 242, 0.1)';

    // Render all entities
    this.entities.forEach(entity => this.renderEntity(entity));

    // Render temporary entity
    if (this.tempEntity) {
      this.ctx.strokeStyle = '#BF5AF2';
      this.ctx.globalAlpha = 0.7;
      this.renderEntity(this.tempEntity);
      this.ctx.globalAlpha = 1.0;
    }

    // Render snap point indicator
    if (this.snapPoint && this.snapMode) {
      const snapPos = this.worldToCanvas(this.snapPoint.point);

      this.ctx.save();
      this.ctx.strokeStyle = '#00FF00';
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(snapPos.x, snapPos.y, 8, 0, Math.PI * 2);
      this.ctx.stroke();

      // Draw snap type indicator
      this.ctx.fillStyle = '#00FF00';
      this.ctx.font = '10px Inter, sans-serif';
      this.ctx.fillText(this.snapPoint.type, snapPos.x + 12, snapPos.y - 12);
      this.ctx.restore();
    }
  }

  renderEntity(entity) {
    // Set dashed line for construction geometry
    if (entity.construction) {
      this.ctx.setLineDash([10, 5]);
      this.ctx.strokeStyle = '#FFA500'; // Orange for construction
      this.ctx.globalAlpha = 0.6;
    } else {
      this.ctx.setLineDash([]);
      this.ctx.strokeStyle = '#BF5AF2'; // Purple for regular
      this.ctx.globalAlpha = 1.0;
    }

    switch (entity.type) {
      case 'point':
        this.renderPoint(entity);
        break;
      case 'line':
        this.renderLine(entity);
        break;
      case 'rectangle':
        this.renderRectangle(entity);
        break;
      case 'circle':
        this.renderCircle(entity);
        break;
      case 'arc':
        this.renderArc(entity);
        break;
      case 'spline':
        this.renderSpline(entity);
        break;
      case 'text':
        this.renderText(entity);
        break;
      case 'ellipse':
        this.renderEllipse(entity);
        break;
      case 'polygon':
        this.renderPolygon(entity);
        break;
      case 'slot':
        this.renderSlot(entity);
        break;
    }

    // Reset styles
    this.ctx.setLineDash([]);
    this.ctx.globalAlpha = 1.0;
  }

  renderPoint(point) {
    const pos = this.worldToCanvas(point.position);

    this.ctx.beginPath();
    this.ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.stroke();
  }

  renderLine(line) {
    const start = this.worldToCanvas(line.start);
    const end = this.worldToCanvas(line.end);

    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.stroke();
  }

  renderRectangle(rect) {
    const start = this.worldToCanvas(rect.start);
    const end = this.worldToCanvas(rect.end);

    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    this.ctx.beginPath();
    this.ctx.rect(x, y, width, height);
    this.ctx.stroke();
  }

  renderCircle(circle) {
    const center = this.worldToCanvas(circle.center);

    // Calculate radius in screen space
    const radiusPoint = {
      x: circle.center.x + circle.radius,
      y: circle.center.y,
      z: circle.center.z
    };
    const radiusScreen = this.worldToCanvas(radiusPoint);
    const screenRadius = Math.abs(radiusScreen.x - center.x);

    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, screenRadius, 0, Math.PI * 2);
    this.ctx.stroke();
  }

  renderArc(arc) {
    const center = this.worldToCanvas(arc.center);
    const start = this.worldToCanvas(arc.start);
    const end = this.worldToCanvas(arc.end);

    const screenRadius = Math.sqrt(
      Math.pow(start.x - center.x, 2) + Math.pow(start.y - center.y, 2)
    );

    const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
    const endAngle = Math.atan2(end.y - center.y, end.x - center.x);

    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, screenRadius, startAngle, endAngle);
    this.ctx.stroke();
  }

  renderSpline(spline) {
    if (spline.points.length < 2) return;

    const points = spline.points.map(p => this.worldToCanvas(p));

    this.ctx.beginPath();
    this.ctx.moveTo(points[0].x, points[0].y);

    // Draw smooth curve through points
    for (let i = 1; i < points.length - 1; i++) {
      const xc = (points[i].x + points[i + 1].x) / 2;
      const yc = (points[i].y + points[i + 1].y) / 2;
      this.ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
    }

    // Last segment
    if (points.length > 1) {
      const last = points[points.length - 1];
      const prev = points[points.length - 2];
      this.ctx.quadraticCurveTo(prev.x, prev.y, last.x, last.y);
    }

    this.ctx.stroke();
  }

  renderText(textEntity) {
    const pos = this.worldToCanvas(textEntity.position);

    this.ctx.save();
    this.ctx.font = '16px Inter, sans-serif';
    this.ctx.fillStyle = '#BF5AF2';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(textEntity.text, pos.x, pos.y);
    this.ctx.restore();
  }

  renderEllipse(ellipse) {
    const center = this.worldToCanvas(ellipse.center);

    // Calculate radii in screen space
    const rxPoint = {
      x: ellipse.center.x + ellipse.radiusX,
      y: ellipse.center.y,
      z: ellipse.center.z
    };
    const ryPoint = {
      x: ellipse.center.x,
      y: ellipse.center.y + ellipse.radiusY,
      z: ellipse.center.z
    };

    const rxScreen = this.worldToCanvas(rxPoint);
    const ryScreen = this.worldToCanvas(ryPoint);
    const screenRx = Math.abs(rxScreen.x - center.x);
    const screenRy = Math.abs(ryScreen.y - center.y);

    this.ctx.beginPath();
    this.ctx.ellipse(center.x, center.y, screenRx, screenRy, 0, 0, Math.PI * 2);
    this.ctx.stroke();
  }

  renderPolygon(polygon) {
    const center = this.worldToCanvas(polygon.center);

    // Calculate radius in screen space
    const radiusPoint = {
      x: polygon.center.x + polygon.radius,
      y: polygon.center.y,
      z: polygon.center.z
    };
    const radiusScreen = this.worldToCanvas(radiusPoint);
    const screenRadius = Math.abs(radiusScreen.x - center.x);

    this.ctx.beginPath();

    for (let i = 0; i <= polygon.sides; i++) {
      const angle = (i * 2 * Math.PI) / polygon.sides - Math.PI / 2;
      const x = center.x + screenRadius * Math.cos(angle);
      const y = center.y + screenRadius * Math.sin(angle);

      if (i === 0) {
        this.ctx.moveTo(x, y);
      } else {
        this.ctx.lineTo(x, y);
      }
    }

    this.ctx.stroke();
  }

  renderSlot(slot) {
    const start = this.worldToCanvas(slot.start);
    const end = this.worldToCanvas(slot.end);

    const width = Math.abs(end.y - start.y);
    const length = Math.abs(end.x - start.x);
    const radius = width / 2;

    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);

    this.ctx.beginPath();

    // Left semicircle
    this.ctx.arc(x + radius, y + radius, radius, Math.PI / 2, Math.PI * 1.5);
    // Top line
    this.ctx.lineTo(x + length - radius, y);
    // Right semicircle
    this.ctx.arc(x + length - radius, y + radius, radius, Math.PI * 1.5, Math.PI / 2);
    // Bottom line
    this.ctx.lineTo(x + radius, y + width);

    this.ctx.stroke();
  }

  // ===== 3D Visualization =====

  /**
   * Update 3D sketch visualization in real-time
   */
  update3DSketch() {
    if (!this.activeSketch || this.entities.length === 0) return;

    // Remove existing preview if any
    const existingPreview = this.cadViewer.cadObjects.get('sketch_preview');
    if (existingPreview) {
      this.engine.scene.remove(existingPreview);
    }

    // Create updated 3D visualization
    const points = [];

    this.entities.forEach(entity => {
      switch (entity.type) {
        case 'line':
          points.push(new THREE.Vector3(entity.start.x, entity.start.y, entity.start.z));
          points.push(new THREE.Vector3(entity.end.x, entity.end.y, entity.end.z));
          break;

        case 'rectangle':
          const corners = this.getRectangleCorners(entity);
          corners.forEach((corner, i) => {
            points.push(corner);
            points.push(corners[(i + 1) % corners.length]);
          });
          break;

        case 'circle':
          const circlePoints = this.getCirclePoints(entity, 32);
          circlePoints.forEach((point, i) => {
            points.push(point);
            points.push(circlePoints[(i + 1) % circlePoints.length]);
          });
          break;

        case 'point':
          // Add small crosshair for point
          const p = entity.position;
          const size = 0.05;
          points.push(new THREE.Vector3(p.x - size, p.y, p.z));
          points.push(new THREE.Vector3(p.x + size, p.y, p.z));
          points.push(new THREE.Vector3(p.x, p.y - size, p.z));
          points.push(new THREE.Vector3(p.x, p.y + size, p.z));
          break;
      }
    });

    if (points.length > 0) {
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0xBF5AF2,
        linewidth: 3,
        transparent: true,
        opacity: 0.9
      });

      const mesh = new THREE.LineSegments(geometry, material);
      mesh.userData = {
        isCADSketchPreview: true
      };

      this.engine.scene.add(mesh);
      this.cadViewer.cadObjects.set('sketch_preview', mesh);
    }
  }

  create3DSketch(sketch) {
    console.log('[SketchEngine] Creating 3D visualization of sketch');

    // Separate regular and construction entities
    const regularPoints = [];
    const constructionPoints = [];

    sketch.entities.forEach(entity => {
      const points = entity.construction ? constructionPoints : regularPoints;
      switch (entity.type) {
        case 'line':
          points.push(new THREE.Vector3(entity.start.x, entity.start.y, entity.start.z));
          points.push(new THREE.Vector3(entity.end.x, entity.end.y, entity.end.z));
          break;

        case 'rectangle':
          const corners = this.getRectangleCorners(entity);
          corners.forEach((corner, i) => {
            points.push(corner);
            points.push(corners[(i + 1) % corners.length]);
          });
          break;

        case 'circle':
          const circlePoints = this.getCirclePoints(entity, 32);
          circlePoints.forEach((point, i) => {
            points.push(point);
            points.push(circlePoints[(i + 1) % circlePoints.length]);
          });
          break;
      }
    });

    // Create regular geometry mesh
    if (regularPoints.length > 0) {
      const geometry = new THREE.BufferGeometry().setFromPoints(regularPoints);
      const material = new THREE.LineBasicMaterial({
        color: 0xBF5AF2,
        linewidth: 2
      });

      const mesh = new THREE.LineSegments(geometry, material);
      mesh.userData = {
        isCADSketch: true,
        sketchId: sketch.id,
        sketchName: sketch.name
      };

      this.engine.scene.add(mesh);
      this.cadViewer.cadObjects.set(sketch.id, mesh);
    }

    // Create construction geometry mesh (dashed lines)
    if (constructionPoints.length > 0) {
      const geometry = new THREE.BufferGeometry().setFromPoints(constructionPoints);
      const material = new THREE.LineDashedMaterial({
        color: 0xFFA500, // Orange for construction
        linewidth: 1,
        dashSize: 0.1,
        gapSize: 0.05,
        transparent: true,
        opacity: 0.6
      });

      const mesh = new THREE.LineSegments(geometry, material);
      mesh.computeLineDistances(); // Required for dashed lines
      mesh.userData = {
        isCADSketchConstruction: true,
        sketchId: sketch.id,
        sketchName: sketch.name
      };

      this.engine.scene.add(mesh);
      this.cadViewer.cadObjects.set(sketch.id + '_construction', mesh);
    }

    return this.cadViewer.cadObjects.get(sketch.id);
  }

  getRectangleCorners(rect) {
    const { start, end } = rect;
    return [
      new THREE.Vector3(start.x, start.y, start.z),
      new THREE.Vector3(end.x, start.y, start.z),
      new THREE.Vector3(end.x, end.y, end.z),
      new THREE.Vector3(start.x, end.y, start.z)
    ];
  }

  getCirclePoints(circle, segments = 32) {
    const points = [];
    const { center, radius } = circle;

    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(
        center.x + Math.cos(angle) * radius,
        center.y + Math.sin(angle) * radius,
        center.z
      ));
    }

    return points;
  }
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { SketchEngine };
}
